import { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, StripeElementsOptions } from "@stripe/stripe-js";

import { PaymentPlan, Policyholder } from "@covertree/new-business-graphql";
import { InfoDisclaimerBox } from "@covertree/ui/content";
import { PaymentSummaryPricing } from "@covertree/ui";

import { MortgageBillPayment } from "./MortgageBillPayment";
import { StripePayment } from "./StripePayment";
import { PaymentMethodTabs, PaymentMethodTabsProps } from "./PaymentMethodTabs";

const stripePromise = loadStripe(process.env.VITE_STRIPE_PK_KEY!);

interface Props extends PaymentMethodTabsProps {
  mortgageBillAvailable: boolean;
  selectedPaymentPlan: PaymentPlan;
  policyholder: Policyholder;
  pricing: PaymentSummaryPricing;
  isD2C?: boolean;
}

export function PaymentMethod({
  mortgageBillAvailable,
  onPaymentMethodChange,
  policyholder,
  pricing,
  selectedPaymentPlan,
  selectedPaymentPlanKey,
  isD2C = false,
}: Props) {
  const stripeOptions = useMemo<StripeElementsOptions>(
    () => ({
      appearance: {
        theme: "flat",
        labels: "above",
        variables: {
          colorPrimary: "#006B5B",
          colorWarning: "#F47A35",
          colorDanger: "#DA2C2C",
          colorText: "#000000",
          fontFamily:
            'PolySans, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif',
          tabSpacing: "var(--p-spacing5)",
          gridRowSpacing: "var(--p-spacing4)",
        },
      },
      locale: "en",
      mode: "payment",
      amount: Math.round((pricing.dueToday ?? 0) * 100),
      paymentMethodOptions: {
        us_bank_account: {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          verification_method: "skip",
        },
      },
      setupFutureUsage: "off_session",
      currency: "usd",
    }),
    [pricing.dueToday]
  );

  return (
    <Box mt={3}>
      <Typography variant="h6" gutterBottom>
        Payment method
      </Typography>
      {mortgageBillAvailable && (
        <PaymentMethodTabs
          onPaymentMethodChange={onPaymentMethodChange}
          selectedPaymentPlanKey={selectedPaymentPlanKey}
        />
      )}
      {selectedPaymentPlanKey !== "mortgageBill" && !isD2C && (
        <InfoDisclaimerBox my={3}>
          <strong>Important:</strong> By selecting <strong>ACH</strong> or{" "}
          <strong>Credit Card</strong> as the payment method, you confirm that
          the customer agrees to be enrolled in{" "}
          <strong>automatic payments</strong>.
        </InfoDisclaimerBox>
      )}
      <Elements stripe={stripePromise} options={stripeOptions}>
        {selectedPaymentPlanKey === "mortgageBill" ? (
          <MortgageBillPayment selectedPaymentPlan={selectedPaymentPlan} />
        ) : (
          <StripePayment
            policyholder={policyholder}
            selectedPaymentPlanKey={selectedPaymentPlanKey}
            selectedPaymentPlan={selectedPaymentPlan}
          />
        )}
      </Elements>
    </Box>
  );
}
